import { MUTATION, useMutationOperation } from 'api/Mutation';
import { QUERY, useQueryOperation } from 'api/Query';
import { ServiceCallError } from 'api/ServiceCallError';
import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import type { CodeLineSelection } from 'ts/commons/code/CodeLineSelection';
import { DateAndBranchChooser } from 'ts/commons/components/DateAndBranchChooser';
import { GlobalFormErrors } from 'ts/commons/forms/GlobalFormErrors';
import { PartitionInput } from 'ts/commons/forms/PartitionInput';
import { PartitionNameUtils } from 'ts/commons/PartitionNameUtils';
import { Button } from 'ts/components/Button';
import { Form, FormInput } from 'ts/components/Form';
import { Modal, ModalActionButtons } from 'ts/components/Modal';
import { ToastNotification } from 'ts/components/Toast';
import { EReportFormat } from 'typedefs/EReportFormat';

type MarkAsCoveredValues = {
	partition: string;
	timestamp: number | null;
	branch: string | null;
	selectedLines: string;
};

/** Dialog to configure the lines that should be covered. */
export function MarkAsCoveredDialog({
	project,
	commit,
	uniformPath,
	onClose,
	selectedLines,
	isOpen
}: {
	project: string;
	commit: UnresolvedCommitDescriptor;
	selectedLines: CodeLineSelection;
	uniformPath: string;
	onClose: () => void;
	isOpen: boolean;
}) {
	const formMethods = useForm<MarkAsCoveredValues>({
		defaultValues: {
			timestamp: commit.timestamp,
			branch: commit.branchName,
			selectedLines: selectedLines.firstLine + '-' + selectedLines.lastLine
		}
	});
	const uploadReport = useMutationOperation(MUTATION.uploadReport, {
		onSuccess: () => {
			ToastNotification.success('Coverage was uploaded. Processing the upload will take some time.');
			onClose();
		},
		onError: err => {
			if (err instanceof ServiceCallError) {
				formMethods.setError('root.global', {
					message: 'Marking lines as covered failed with service error: ' + err.errorSummary
				});
				return;
			}
			formMethods.setError('root.global', { message: 'Unknown error occurred: ' + err });
		}
	}).mutate;

	const onFormSubmit = (data: MarkAsCoveredValues) => {
		formMethods.clearErrors();
		const content = uniformPath + '\n' + data.selectedLines.split(',').join('\n');
		const blob = new Blob([content], { type: 'text/plain' });
		const report = new File([blob], 'simple.txt');
		const uploadCommit = new UnresolvedCommitDescriptor(data.timestamp, data.branch);
		uploadReport({
			pathParams: { project, sessionId: 'auto-create' },
			queryParams: {
				t: uploadCommit,
				partition: data.partition,
				format: EReportFormat.SIMPLE.name
			},
			body: {
				report: [report]
			}
		});
	};

	return (
		<Modal size={550} opened={isOpen} title="Mark lines as covered" onClose={onClose}>
			<Form onSubmit={formMethods.handleSubmit(onFormSubmit)} error>
				<FormProvider {...formMethods}>
					<GlobalFormErrors />
					<DateAndBranchField project={project} />
					<PartitionField project={project} />
					<LineInput />
					<ModalActionButtons>
						<Button type="submit" primary content="Submit coverage" />
					</ModalActionButtons>
				</FormProvider>
			</Form>
		</Modal>
	);
}

function DateAndBranchField({ project }: { project: string }): JSX.Element {
	const { setValue, control } = useFormContext<MarkAsCoveredValues>();
	const timestamp = useWatch({ name: 'timestamp', control });
	const branch = useWatch({ name: 'branch', control });

	return (
		<DateAndBranchChooser
			branchSelectorLabel="Branch"
			datePickerLabel="Code Date"
			datePickerTooltip="This field must be filled with the date of the checkin in your VCS that corresponds
					to the version of your application for which the external report was created. E.g. for findings
					reports this would be the date of the VCS checkin for which the checker tool was run. For coverage
					reports this would be the date of the VCS checkin that was built and deployed to the test system."
			projects={[project]}
			timestamp={timestamp}
			branchName={branch}
			onTimestampChange={newTimestamp => setValue('timestamp', newTimestamp)}
			onBranchNameChange={branchName => setValue('branch', branchName)}
		/>
	);
}

function PartitionField({ project }: { project: string }): JSX.Element {
	const partitionLabelSeparator = PartitionNameUtils.LABEL_AND_PARTITION_NAME_SEPARATOR;
	const analysisPartitions = useQueryOperation(QUERY.getExternalAnalysisStatusPartitions(project)).data;

	return (
		<PartitionInput<MarkAsCoveredValues>
			analysisPartitions={analysisPartitions}
			placeholder="e.g. unit-tests"
			helpText={`Reflects the report source. Use '${partitionLabelSeparator}' to separate a label from the partition name and other labels (e.g. 'UI Tests ${partitionLabelSeparator} Team 1' or 'Unit Tests ${partitionLabelSeparator} Some Project').`}
			name="partition"
			rules={{ required: 'Please select a partition.' }}
		/>
	);
}

function LineInput(): JSX.Element {
	const { register } = useFormContext<MarkAsCoveredValues>();

	return (
		<FormInput
			label="Line numbers"
			input={register('selectedLines', { required: 'Please select lines in format startLine-endLine.' })}
		/>
	);
}
